import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiGet, apiPost } from 'src/lib/api/axios';
import { toast } from 'src/components/ui/toast';
import { DocumentWithExtras } from './types/document';

// Types for element lookup
export interface SelectionBoundingBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  page_number: number;
}

export interface FindElementsResponse {
  element_ids: string[];
}
// ----------------------------------------------------------------------

const initialState: {
  currentDocument: DocumentWithExtras | null;
  userFavouriteDocs: string[];
} = {
  currentDocument: null,
  userFavouriteDocs: []
};

const slice = createSlice({
  name: 'wiki',
  initialState,
  reducers: {
    getUserFavouriteDocs(state, action) {
      state.userFavouriteDocs = action.payload;
    },

    setCurrentDocument(state, { payload }: PayloadAction<DocumentWithExtras>) {
      state.currentDocument = payload;
    },

    setCurrentDocumentFilestem(state, { payload }: PayloadAction<string>) {
      if (state.currentDocument?.document) {
        state.currentDocument.document.filestem = payload;
      }
    }
  }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export const { setCurrentDocumentFilestem } = slice.actions;

export function setFavouriteDoc(external_id: string, like: boolean) {
  return async () => {
    try {
      await apiPost(
        '/docs/set_favourite',
        { external_id, like },
        { meta: { globalError: false, context: 'favourite-doc-toggle' } }
      );
      return { success: true };
    } catch {
      const errorMsg = 'Failed to update favourite document';
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  };
}

export function fetchUserFavouriteDocs({ suppressError = false } = {}) {
  return async (dispatch: any) => {
    try {
      const response = await apiGet('/docs/my-favourites', {
        meta: {
          globalError: false,
          context: 'user-favourite-docs-fetch'
        }
      });
      dispatch(slice.actions.getUserFavouriteDocs(response.data));
    } catch {
      if (!suppressError) {
        toast.error('Failed to fetch users favourite documents');
      }
    }
  };
}

export function fetchDocument(
  identifierOrFilename: string,
  element_ids?: string[]
): (dispatch: any) => Promise<DocumentWithExtras> {
  return async (dispatch) => {
    // Build query params if element_ids are provided
    const params = new URLSearchParams();
    if (element_ids && element_ids.length > 0) {
      element_ids.forEach((id) => params.append('element_ids', id.toString()));
    }
    const queryString = params.toString() ? `?${params.toString()}` : '';

    // Check if the input looks like a ULID (26 characters, alphanumeric)
    const isULID = /^[0-9A-Za-z]{26}$/.test(identifierOrFilename);

    // Use appropriate endpoint based on identifier type
    const endpoint = isULID
      ? `/docs/document/${identifierOrFilename}${queryString}`
      : `/docs/newest-document-by-filename/${identifierOrFilename}${queryString}`;

    const response = await apiGet<DocumentWithExtras>(endpoint, {
      meta: {
        globalError: false,
        context: 'document-fetch'
      }
    });
    dispatch(slice.actions.setCurrentDocument(response.data));
    return response.data;
  };
}

// Types for source writing
export interface SourceWritingInfo {
  external_writing_id: string;
  writing_type: string;
}

interface DocumentLinkResponse {
  url: string;
}

/**
 * Create a fresh, short-lived URL for downloading the document currently being viewed.
 *
 * The view URL expires after ten minutes, so it must not be reused for a download
 * action that may happen much later.
 */
export async function fetchDocumentDownloadLink(
  documentExternalId: string,
  { original }: { original: boolean }
): Promise<string> {
  const params = original ? 'original=true' : 'content_type=application%2Fpdf';
  const response = await apiGet<DocumentLinkResponse>(`/files/${documentExternalId}/link?${params}`, {
    meta: {
      globalError: false,
      context: 'document-download-link-fetch'
    }
  });
  return response.data.url;
}

/**
 * Get the source writing document for a given document external ID.
 * Returns null if the document was not created from a writing document.
 * Only call this for non-wiki documents
 */
export async function fetchSourceWriting(documentExternalId: string): Promise<SourceWritingInfo | null> {
  try {
    const response = await apiGet<SourceWritingInfo>(`/writing/source-for-document/${documentExternalId}`, {
      meta: {
        globalError: false,
        context: 'source-writing-fetch'
      }
    });
    return response.data;
  } catch (error: any) {
    // 404 or other errors mean no source writing - not an error condition
    return null;
  }
}

/**
 * Find element IDs that overlap with the given bounding boxes.
 * This is used when a user selects text in a PDF and wants to copy a link to those elements.
 */
export async function findElementsByBoundingBoxes(
  documentExternalId: string,
  boundingBoxes: SelectionBoundingBox[]
): Promise<string[]> {
  try {
    const response = await apiPost<FindElementsResponse>(
      `/docs/document/${documentExternalId}/find-elements`,
      { bounding_boxes: boundingBoxes },
      {
        meta: {
          globalError: false,
          context: 'find-elements-by-bbox'
        }
      }
    );
    return response.data.element_ids;
  } catch (error: any) {
    console.error('Failed to find elements by bounding boxes:', error);
    return [];
  }
}
